/**
 * The Home opening (contexts/05, "The Home opening"): a four-second clip with its own sound — the
 * raptor close-up fading to white — over the whole window, when a signed-in person reaches Home, at
 * most once every ten days per person and browser. The rules of who sees it and when, pure so they
 * are tested; the player is `components/HomeOpening.tsx`. The browser rule — desktop Chrome, motion
 * allowed — is the Manager Meeting entrance's (`canPlayEntrance`), on purpose: one answer to "which
 * browser plays a clip".
 */

import { readStoredInt, storeInt, type KeyValueStore } from './storedFlag.js';

/** The opening plays at most once this many days apart, per person and browser (Dakota, 2026-11-03). */
export const OPENING_INTERVAL_DAYS = 10;
const OPENING_INTERVAL_MS = OPENING_INTERVAL_DAYS * 24 * 60 * 60 * 1000;

/** No first frame this long after the arrival: Home shows without it. `waiting` before then is normal. */
export const OPENING_STARTUP_TIMEOUT_MS = 4_000;
/** The clip runs 4.06 s; anything still on screen this long after the arrival is dismissed. */
export const OPENING_OVERALL_TIMEOUT_MS = 12_000;
/**
 * The clip's own picture fades to white over its last 0.64 s (3.42–3.97 s of 4.06). The white layer
 * over the whole window — margins included — fades in from this far before the end, so the two
 * reach white together.
 */
export const OPENING_WHITE_LEAD_S = 0.6;
/** At `ended`, white holds this long… */
export const OPENING_WHITE_HOLD_MS = 120;
/** …then fades away over this long, revealing Home underneath (CSS `.opening--revealing`). */
export const OPENING_REVEAL_MS = 350;

export function openingStorageKey(userId: string): string {
  return `unitedos.home_opening.${userId}`;
}

/** Home itself — the index route — and nothing else: no sign-in, no other page, no meeting. */
export function isHomeRoute(pathname: string): boolean {
  return pathname === '/' || pathname === '';
}

/**
 * Whether this arrival should play the opening: Home, a signed-in person (their id is the viewer
 * id), Chrome on a desktop with motion allowed, and not tried in this browser in the last
 * `OPENING_INTERVAL_DAYS` — a Skip, a failure or a refresh counts as tried. `now` is epoch ms.
 *
 * The mark is the epoch ms of the last try, in `localStorage`. Anything else under the key reads as
 * no mark, so it plays (and is marked afresh): absent, junk, a time after `now` (a clock set back),
 * a storage that throws, and the old rule's `'1'` — which only ever went to `sessionStorage`, but
 * would read as 1 ms past 1970 here and so play once.
 */
export function shouldPlayOpening(input: {
  readonly pathname: string;
  readonly userId: string;
  readonly canPlay: boolean;
  readonly store: KeyValueStore | null;
  readonly tried: ReadonlySet<string>;
  readonly now: number;
}): boolean {
  if (!isHomeRoute(input.pathname) || input.userId === '' || !input.canPlay) return false;
  const key = openingStorageKey(input.userId);
  if (input.tried.has(key)) return false;
  const last = readStoredInt(input.store, key, 0, input.now);
  return last === null || input.now - last >= OPENING_INTERVAL_MS;
}

/**
 * Marks the attempt before playback: its time in the browser's storage, and the key in memory —
 * where storage refuses (a private window) the memory mark alone holds, so it plays once per page load.
 */
export function markOpeningTried(userId: string, store: KeyValueStore | null, tried: Set<string>, now: number): void {
  const key = openingStorageKey(userId);
  tried.add(key);
  storeInt(store, key, now);
}

/** Whether the clip has reached its fade to white, where the white layer joins it. */
export function inWhiteWindow(currentTime: number, duration: number): boolean {
  return Number.isFinite(duration) && duration > 0 && duration - currentTime <= OPENING_WHITE_LEAD_S;
}
